import { Close, Refresh } from "@mui/icons-material"
import {
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import moment from "moment"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Locker } from "shared/locker"
import ObjectDisplayTable from "../ObjectDisplayTable"

/**
 * The raw PARTNER snapshot — `sstLocker` on v1, `sst2Locker` on v2.
 *
 * Extracted from `SaLockerPage.tsx` by LOCKER-355, which found that file at 432
 * lines, over the repo's ~400-line component cap, and needed to add to it. The
 * move is mechanical: same markup, same props, no behaviour change.
 *
 * Note what this is NOT: it shows only the partner's half of the object. Every
 * Mobilus-side field that decides how the Unit section behaves — `sstApiVersion`,
 * `moduleMapping`, the migration markers — lives outside it, which is what
 * `SaLockerObjectViewer` exists to show.
 */
export const SaLockerSnapshotDrawer: FC<{
  open: boolean
  onClose: () => void
  isV2: boolean
  locker?: Locker
  onRefresh: () => void
  isRefreshing?: boolean
}> = ({ open, onClose, isV2, locker, onRefresh, isRefreshing }) => {
  const { t } = useTranslation()
  return (
    <Drawer data-ui="UI-SL2W-155"
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100vw", md: "60vw" },
          maxWidth: 1200,
          pt: "64px",
        },
      }}
    >
      <Stack direction="column" spacing={2} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              {isV2 ? "SST V2" : "SST V1"} — {t("saLockerConsole.rawSnapshot")}
            </Typography>
            <Tooltip title={t("saLockerConsole.syncFromSst")}>
              <IconButton onClick={onRefresh} disabled={isRefreshing} size="small">
                {isRefreshing ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
        {locker?.lastSstSync && (
          <Typography variant="caption" color="text.secondary">
            {t("lastSync")}: {moment(locker.lastSstSync).format("YYYY-MM-DD HH:mm:ss")} (
            {moment(locker.lastSstSync).fromNow()})
          </Typography>
        )}
        <Divider />
        <ObjectDisplayTable data={isV2 ? locker?.sst2Locker : locker?.sstLocker} />
      </Stack>
    </Drawer>
  )
}

export default SaLockerSnapshotDrawer
